import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Button,
  Table,
  Modal,
  Row,
  Col,
} from "react-bootstrap";
import axios from "axios";

const LogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [showClearLogModal, setShowClearLogModal] = useState(false);

  // Fetch logs on mount
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/logs`
        );
        // Assuming logs are returned as a newline-separated string of JSON objects
        const logArray = response.data
          .split("\n")
          .filter((line) => line.trim())
          .map((line) => {
            try {
              return JSON.parse(line);
            } catch {
              return {
                level: "error",
                message: "Invalid log format",
                timestamp: new Date().toISOString(),
              };
            }
          });
        setLogs(logArray);
      } catch (error) {
        console.error("Error fetching logs:", error);
        setLogs([
          {
            level: "error",
            message: "Failed to load logs",
            timestamp: new Date().toISOString(),
          },
        ]);
      }
    };
    fetchLogs();
  }, []);

  // Handle clear log action
  const handleClearLog = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_SERVER_URL}/logs`);
      setLogs([]);
      alert("Log file cleared successfully");
      setShowClearLogModal(false);
    } catch (error) {
      console.error("Error clearing logs:", error);
      alert("Failed to clear logs");
      setShowClearLogModal(false);
    }
  };

  // Handle download log action
  const handleDownloadLog = () => {
    const logText = logs.map((log) => JSON.stringify(log)).join("\n");
    const blob = new Blob([logText], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `TradingLog_${new Date().toISOString().split("T")[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Map log levels to Bootstrap variants for styling
  const getLevelVariant = (level) => {
    switch (level.toLowerCase()) {
      case "info":
        return "bg-info text-white";
      case "warn":
        return "bg-warning text-dark";
      case "error":
        return "bg-danger text-white";
      default:
        return "bg-secondary text-white";
    }
  };

  return (
    <Container className="shadow p-4 mt-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2>Trading Log</h2>
        </Col>
        <Col xs="auto">
          <Button
            variant="success"
            onClick={handleDownloadLog}
            disabled={
              logs.length === 0 || logs[0]?.message === "Failed to load logs"
            }
            className="me-2"
          >
            Download Log
          </Button>
          <Button
            variant="danger"
            onClick={() => setShowClearLogModal(true)}
            disabled={
              logs.length === 0 || logs[0]?.message === "Failed to load logs"
            }
          >
            Clear Log
          </Button>
        </Col>
      </Row>
      <Card>
        <Card.Body>
          <div
            className="table-responsive"
            style={{ maxHeight: "500px", overflowY: "auto" }}
          >
            <Table striped bordered hover size="sm">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: "15%" }}>Level</th>
                  <th style={{ width: "50%" }}>Message</th>
                  <th style={{ width: "35%" }}>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {logs.length > 0 ? (
                  logs.map((log, index) => (
                    <tr key={index}>
                      <td className={getLevelVariant(log.level)}>
                        {log.level.toUpperCase()}
                      </td>
                      <td>{log.message}</td>
                      <td>
                        {new Date(log.timestamp).toLocaleString("en-IN", {
                          dateStyle: "short",
                          timeStyle: "medium",
                        })}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center">
                      No logs available
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Modal
        show={showClearLogModal}
        onHide={() => setShowClearLogModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Clear Logs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to clear all logs?
          <br />
          <strong>
            This action will permanently remove {logs.length} log(s) and cannot
            be undone.
          </strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleClearLog} className="me-2">
            Confirm
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowClearLogModal(false)}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LogViewer;
